import {
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Fragment, useEffect, useMemo } from "react";
import { useStockCategoryQueries } from "../../StockCategoriesAndTags/hooks/useStockCategoryQueries";
import emptyIcon from "../../../assets/images/Icons/search-table.png";

export const StockCategoryItems = ({
  selectedCategoryId,
  onSelect,
  groupedCategories = [],
}) => {
  const {
    listQuery: { data, isLoading },
  } = useStockCategoryQueries({ categorylist: true });

  const filteredCategories = useMemo(() => {
    const groupedIds = new Set(groupedCategories.map((g) => g.id));

    return (
      data
        ?.filter((cat) => groupedIds.has(cat.id))
        ?.map(({ name, id }) => ({ name, id })) || []
    );
  }, [data, groupedCategories]);

  // Auto-select first category once data loads or when filtered list changes
  useEffect(() => {
    if (filteredCategories.length > 0) {
      const isSelectedVisible = filteredCategories.some(
        (c) => c.id === selectedCategoryId,
      );
      if (!selectedCategoryId || !isSelectedVisible) {
        onSelect?.(filteredCategories[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCategories.length, selectedCategoryId]);

  return (
    <Stack>
      <Typography sx={{ fontSize: "14px", fontWeight: 500, mb: 1 }}>
        Category
      </Typography>

      {isLoading ? (
        <Stack alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : filteredCategories.length === 0 ? (
        <Stack alignItems="center" gap="8px" justifyContent="center" py={4}>
          <img src={emptyIcon} alt="empty" height={40} width={40} />
          <Typography
            sx={{ fontWeight: 400, fontSize: "14px", color: "#485364" }}
          >
            No categories found
          </Typography>
        </Stack>
      ) : (
        <List
          disablePadding
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          {filteredCategories.map((category, index) => (
            <Fragment key={category.id}>
              <ListItemButton
                selected={selectedCategoryId === category.id}
                onClick={() => onSelect?.(category)}
                sx={{
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                  minHeight: "36px",
                }}
              >
                <ListItemText
                  primary={category.name}
                  primaryTypographyProps={{ fontSize: "14px", fontWeight: 400 }}
                />
                <Stack direction="row" alignItems="center" gap="8px">
                  <Typography fontSize="14px" fontWeight={400}>
                    {groupedCategories.find((g) => g.id === category.id)
                      ?.subcategories?.length ?? 0}
                  </Typography>
                  <ArrowForwardIosIcon sx={{ fontSize: "10px" }} />
                </Stack>
              </ListItemButton>
              {index < filteredCategories.length - 1 && <Divider />}
            </Fragment>
          ))}
        </List>
      )}
    </Stack>
  );
};
